import { afterRenderEffect, Component, DOCUMENT, effect, ElementRef, inject, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { HEADER_ATTACHED_ROUTE_DATA } from './constants/layout.constant';
import { ConnectionStateComponent } from './components/connection-state/connection-state.component';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { SideMenuComponent } from './components/side-menu/side-menu.component';
import { SplashScreenComponent } from './components/splash-screen/splash-screen.component';
import { ToastHostComponent } from './components/toast-host/toast-host.component';
import { WalletMiniComponent } from './modules/wallet/components/wallet-mini/wallet-mini.component';
import { LayoutService } from './services/layout.service';
import { EVENT_DRIVEN_STARTUP } from './providers/event-driven-startup.providers';

@Component({
	imports: [
		RouterOutlet,
		HeaderComponent,
		FooterComponent,
		SplashScreenComponent,
		ToastHostComponent,
		ConnectionStateComponent,
		SideMenuComponent,
		WalletMiniComponent
	],
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrl: './app.component.scss'
})
export class AppComponent {
	/** Desktop swaps the footer for the side menu and mini-wallet. */
	readonly isDesktop = inject(LayoutService).isDesktop;

	/**
	 * Whether قدرت خرید sits on the column's left instead of under the side menu: the
	 * screen is too short for the two stacked, and wide enough for a panel each side.
	 * Measured rather than a height breakpoint, so it follows the cards' real heights.
	 */
	readonly walletBeside = signal(false);

	private readonly shell = viewChild.required<ElementRef<HTMLElement>>('shell');
	private readonly column = viewChild.required<ElementRef<HTMLElement>>('column');
	private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');
	private readonly sideMenu = viewChild('sideMenu', { read: ElementRef<HTMLElement> });
	private readonly wallet = viewChild('wallet', { read: ElementRef<HTMLElement> });

	private readonly router = inject(Router);

	/** Whether the page on screen opts its top section into the app bar — see {@link HEADER_ATTACHED_ROUTE_DATA}. */
	readonly headerAttached = toSignal(
		this.router.events.pipe(
			filter(event => event instanceof NavigationEnd),
			map(() => this.deepest(this.router.routerState.snapshot.root).data[HEADER_ATTACHED_ROUTE_DATA] === true)
		),
		{ initialValue: false }
	);

	constructor() {
		inject(EVENT_DRIVEN_STARTUP);

		// Portaled sheets, toasts and the keyboard align to the column through `--desktop-column-left`.
		const root = inject(DOCUMENT).documentElement;
		effect(() => root.classList.toggle('desktop-wallet-beside', this.walletBeside()));

		// Re-observe whenever the menu or the mini-wallet is (re)rendered; the observer
		// also fires once on `observe`, which covers the first layout.
		afterRenderEffect(onCleanup => {
			if (typeof ResizeObserver === 'undefined') {
				return;
			}

			const observer = new ResizeObserver(() => this.measureWalletPlacement());
			[this.shell(), this.sideMenu(), this.wallet()].forEach(ref => ref && observer.observe(ref.nativeElement));
			onCleanup(() => observer.disconnect());
		});
	}

	/**
	 * Uses only sizes that don't change with the placement — the cards keep the panel's
	 * width on either side — so moving the mini-wallet never flips the decision back.
	 */
	private measureWalletPlacement(): void {
		const panel = this.panel()?.nativeElement;
		const menu = this.sideMenu()?.nativeElement;
		const wallet = this.wallet()?.nativeElement;
		if (!this.isDesktop() || !panel || !menu || !wallet) {
			this.walletBeside.set(false);
			return;
		}

		const shell = this.shell().nativeElement;
		const shellStyle = getComputedStyle(shell);
		const height = shell.clientHeight - parseFloat(shellStyle.paddingTop) - parseFloat(shellStyle.paddingBottom);
		const width = shell.clientWidth - parseFloat(shellStyle.paddingLeft) - parseFloat(shellStyle.paddingRight);

		const stackedHeight = menu.offsetHeight + parseFloat(getComputedStyle(panel).rowGap) + wallet.offsetHeight;
		const besideWidth =
			this.column().nativeElement.offsetWidth + 2 * (panel.offsetWidth + parseFloat(shellStyle.columnGap));

		this.walletBeside.set(stackedHeight > height && besideWidth <= width);
	}

	private deepest(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
		return route.firstChild ? this.deepest(route.firstChild) : route;
	}
}
